import {apiService} from '../apiService';

const getFormResponsesBase = {
    transformErrorResponse: (_) => ({message: 'Failed to retrieve form requests'}),
    transformResponse: (response) => ({
        ...response,
        items: response.items.map(({data, ...rest}) => ({
            data: data ? JSON.parse(data) : undefined,
            ...rest
        }))
    })
};

const getCommunicationFormsBase = {
    transformErrorResponse: (_) => ({message: 'Failed to retrieve communication forms'}),
    transformResponse: (response) => response // Communication forms don't need data parsing
};

const parseJsonSafe = (value) => {
    if (!value) return {};
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

const normalisePlaceholderValues = (response) => {
    const parsed = parseJsonSafe(response.placeholders);

    const fallback = {
        schoolName: response.schoolName,
        studentForename: response.studentForename,
        studentFullName: response.studentFullName,
        hisHer: response.hisHer,
        heShe: response.heShe,
        himHer: response.himHer,
        title: response.title,

        parentFullName: response.parentFullName,
        parentSurname: response.parentSurname,

        employeeFullName: response.employeeFullName,

        parentAddress: response.parentAddress,
        parentEmail: response.parentEmail,
        parentPhone: response.parentPhone,
        previousSchoolEmail: response.previousSchoolEmail,
        previousSchoolName: response.previousSchoolName,
        studentAddress: response.studentAddress,
        studentBoarderStatus: response.studentBoarderStatus,
        studentDOB: response.studentDOB,
        studentEmail: response.studentEmail,
        studentForm: response.studentForm,
        studentHouse: response.studentHouse,
        studentLegalForename: response.studentLegalForename,
        studentLegalFullName: response.studentLegalFullName,
        studentUPN: response.studentUPN,
        studentYearGroup: response.studentYearGroup,
        studentApplicationStatus: response.studentApplicationStatus,
        studentIntakeGroup: response.studentIntakeGroup,

        employeeSurname: response.employeeSurname,
        employeeAddress: response.employeeAddress,
        employeeDOB: response.employeeDOB,
        employeeEmail: response.employeeEmail,
        employeeLegalFullName: response.employeeLegalFullName,
        employeeLegalSurname: response.employeeLegalSurname,
        employeePhone: response.employeePhone,
    };

    const merged = {
        ...fallback,
        ...parsed,
    };

    if (!merged.parentFullName && merged.parent) merged.parentFullName = merged.parent;
    if (!merged.parent && merged.parentFullName) merged.parent = merged.parentFullName;

    if (!merged.employeeFullName && merged.employee) merged.employeeFullName = merged.employee;
    if (!merged.employee && merged.employeeFullName) merged.employee = merged.employeeFullName;

    return merged;
};

const getFormRequestBaseOptions = {
    transformResponse: (response) => {
        const {
            id,
            name,
            schema,
            data,
            status,
            studentId,
            isParent,
            isCommunication,
            profileMustBeComplete,
            isProfileComplete,
            needsToConfirmSections,
        } = response;

        return {
            formId: id,
            formName: name,
            schema: schema ? JSON.parse(schema) : {},
            data: data ? JSON.parse(data) : {},
            isParent,
            placeholderValues: normalisePlaceholderValues(response),
            status,
            studentId,
            isCommunication,
            profileMustBeComplete: !!profileMustBeComplete,
            isProfileComplete: isProfileComplete == null ? true : !!isProfileComplete,
            needsToConfirmSections: needsToConfirmSections ?? null,
        };
    },
    transformErrorResponse: (error) => {
        const errorMessages = {
            500: 'Internal server error.',
            404: 'Could not find the requested form.'
        };

        return {
            statusText: error.status,
            message: errorMessages[error.status] ?? 'Something went wrong.'
        };
    }
};

export const formRequestsService = apiService.injectEndpoints({
    endpoints: (builder) =>
        ({
            getFormRequests: builder.query({
                query: (body) => ({
                    url: `/forms/${body.formId}/requests`,
                    method: 'POST',
                    data: body,
                }),
                providesTags: (_) => [{type: 'FormRequests', id: 'LIST'}],
                ...getFormResponsesBase
            }),
            getFormRequest: builder.query({
                query: (requestId) => {
                    return ({url: `/forms/requests/${requestId}`});
                },
                ...getFormRequestBaseOptions
            }),
            getMyFormRequest: builder.query({
                query: (requestId) => ({
                    url: `/forms/my/requests/${requestId}`
                }),
                ...getFormRequestBaseOptions
            }),
            getStudentNotificationFormRequest: builder.query({
                query: ({formId, personId}) => ({
                    url: `/forms/notification/${formId}/students/${personId}`
                }),
                ...getFormRequestBaseOptions
            }),
            getStudentNotificationFormResponses: builder.query({
                query: ({formId, personId}) => ({
                    url: `/forms/notification/${formId}/students/${personId}/responses`
                })
            }),
            getEmployeeNotificationFormRequest: builder.query({
                query: ({formId, personId}) => ({
                    url: personId
                        ? `/forms/notification/${formId}/employee/${personId}`
                        : `/forms/notification/${formId}/employee`
                }),
                ...getFormRequestBaseOptions
            }),
            getEmployeeNotificationFormResponses: builder.query({
                query: ({formId, personId}) => ({
                    url: `/forms/notification/${formId}/employee/${personId}/responses`
                })
            }),
            submitForm: builder.mutation({
                query: (body) => ({
                    url: '/forms/submit',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    data: body,
                }),
                transformErrorResponse: (error) => {
                    const defaultErrorMessage = ['Failed to submit form.'];
                    const errorMessages = {
                        400: error?.data?.validationErrors
                            ? error.data.validationErrors.map(x => x.errorMessage).join('; ')
                            : defaultErrorMessage,
                        409: ['This form is no longer available. It may be inactive, expired, or already submitted. Please refresh the page and try again.']
                    };

                    return errorMessages[error.status] ?? defaultErrorMessage;
                }
            }),
            submitPublicForm: builder.mutation({
                query: (body) => ({
                    url: '/forms/public/submit-form',
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    data: body,
                }),
                transformErrorResponse: (error) => {
                    const defaultMessage = ['Failed to submit form.'];

                    if (error.status === 400 && typeof error?.data === 'string') {
                        return [error.data];
                    }
                    if (error.status === 400 && error?.data?.validationErrors) {
                        return error.data.validationErrors.map(x => x.errorMessage).join('; ');
                    }

                    return defaultMessage;
                }
            }),
            getFormsByStudent: builder.query({
                query: (body) => ({
                    url: `/forms/requests/student/${body.studentId}`,
                    method: 'POST',
                    data: body,
                }),
                ...getFormResponsesBase
            }),
            getFormsByEmployee: builder.query({
                query: (body) => ({
                    url: `/forms/requests/employee/${body.employeeId}`,
                    method: 'POST',
                    data: body,
                }),
                ...getFormResponsesBase
            }),
            getCommunicationFormsByStudent: builder.query({
                query: (body) => ({
                    url: `/forms/requests/communication/student/${body.studentId}`,
                    method: 'POST',
                    data: body,
                }),
                ...getCommunicationFormsBase
            }),
            getCommunicationFormsByEmployee: builder.query({
                query: (body) => ({
                    url: `/forms/requests/communication/employee/${body.employeeId}`,
                    method: 'POST',
                    data: body,
                }),
                ...getCommunicationFormsBase
            }),
            getStudentNotificationForms: builder.query({
                query: (studentId) => ({
                    url: `/forms/notification/student/${studentId}`
                }),
                transformResponse: (response) => response?.map(({formName, ...rest}) => ({
                    name: formName,
                    ...rest
                }))
            }),
            getEmployeeNotificationForms: builder.query({
                query: (employeeId) => ({
                    url: `/forms/notification/employee/${employeeId}`
                }),
                transformResponse: (response) => response?.map(({formName, ...rest}) => ({
                    name: formName,
                    ...rest
                }))
            }),
            getMyForms: builder.query({
                query: (body) => ({
                    url: '/forms/my/requests',
                    method: 'POST',
                    data: body,
                }),
                ...getFormResponsesBase
            }),
            getMyCommunicationForms: builder.query({
                query: (body) => ({
                    url: '/forms/my/communication-requests',
                    method: 'POST',
                    data: body,
                }),
                ...getCommunicationFormsBase
            }),
            getMyNotificationForms: builder.query({
                query: () => ({
                    url: '/forms/my/notification-requests'
                })
            }),
            getMyNotificationFormResponses: builder.query({
                query: ({formId}) => ({
                    url: `/forms/my/notification-requests/${formId}/responses`
                })
            }),
            getUsersSubmittedFormRequest: builder.query({
                query: (requestId) => ({
                    url: `/forms/requests/${requestId}/submitted-users`
                })
            }),
            resendFormRequest: builder.mutation({
                query: (body) => ({
                    url: '/forms/request/resend',
                    method: 'POST',
                    data: body,
                }),
                transformErrorResponse: () => 'Failed to resend the form.'
            }),
            requestFormResubmission: builder.mutation({
                query: (body) => ({
                    // Separate endpoint to move request back to resubmission state
                    url: '/forms/request/resubmission',
                    method: 'POST',
                    data: body,
                }),
                transformErrorResponse: () => 'Failed to request resubmission.',
                invalidatesTags: [{type: 'FormRequests', id: 'LIST'}]
            }),
            deleteFormRequest: builder.mutation({
                query: ({formId, requestId}) => ({
                    url: `/forms/${formId}/requests/${requestId}`,
                    method: 'DELETE'
                }),
                transformErrorResponse: () => 'Failed to delete form request.',
                invalidatesTags: [{type: 'FormRequests', id: 'LIST'}]
            }),
            checkContactEmailForStudentRequest: builder.query({
                query: ({
                            requestId,
                            email
                        }) => ({
                    url: `/forms/requests/${requestId}/contact-email-check`,
                    method: 'GET',
                    params: {email}
                }),
                transformErrorResponse: () => ({message: 'Failed to check contact email.'})
            }),
            checkContactEmailForStudentPublicLinkedRequest: builder.query({
                query: ({
                            requestId,
                            email,
                            tag
                        }) => ({
                    url: `/forms/requests/${requestId}/contact-email-check/public`,
                    method: 'GET',
                    params: {
                        email,
                        tag
                    }
                }),
                transformErrorResponse: () => ({message: 'Failed to check contact email.'})
            }),
            checkContactEmailForStudentSelfService: builder.query({
                query: ({
                            formId,
                            studentId,
                            email
                        }) => ({
                    url: `/forms/notification/${formId}/students/${studentId}/contact-email-check`,
                    params: {email}
                })
            }),
        }),
});

export const {
    useGetFormRequestsQuery,
    useGetFormRequestQuery,
    useGetMyFormRequestQuery,
    useGetStudentNotificationFormRequestQuery,
    useGetEmployeeNotificationFormRequestQuery,
    useSubmitFormMutation,
    useSubmitPublicFormMutation,
    useGetFormsByStudentQuery,
    useGetFormsByEmployeeQuery,
    useGetCommunicationFormsByStudentQuery,
    useGetCommunicationFormsByEmployeeQuery,
    useGetStudentNotificationFormsQuery,
    useGetEmployeeNotificationFormsQuery,
    useLazyGetStudentNotificationFormResponsesQuery,
    useLazyGetEmployeeNotificationFormResponsesQuery,
    useGetMyFormsQuery,
    useGetMyCommunicationFormsQuery,
    useGetMyNotificationFormsQuery,
    useLazyGetMyNotificationFormResponsesQuery,
    useGetUsersSubmittedFormRequestQuery,
    useResendFormRequestMutation,
    useRequestFormResubmissionMutation,
    useDeleteFormRequestMutation,
    useLazyCheckContactEmailForStudentRequestQuery,
    useLazyCheckContactEmailForStudentPublicLinkedRequestQuery,
    useLazyCheckContactEmailForStudentSelfServiceQuery,
} = formRequestsService;
